import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FormUtils, GenericObject, Password, Utils } from 'app/core/utils';
import { REGEX_STRONG_PASSWORD } from 'app/shared/constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-password-input',
  templateUrl: './password-input.component.html',
  styleUrls: ['./password-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PasswordInputComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => PasswordInputComponent),
    },
  ],
})
export class PasswordInputComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() checkStrength: boolean = true;
  @Input() type!: Password;
  @Input() isRequired: boolean = false;
  passwordHelpers!: GenericObject;
  subscriptions: Subscription[] = [];
  password: FormControl = new FormControl('');
  isInvalid: boolean = false;

  get label(): string {
    if (this.type == 'password') return 'LABEL.PASSWORD';
    if (this.type == 'confirmPassword') return 'LABEL.CONFIRM_PASSWORD';
    if (this.type == 'oldPassword') return 'LABEL.OLD_PASSWORD';
    return 'LABEL.NEW_PASSWORD';
  }

  constructor(private translate: TranslateService) {
    this.passwordHelpers = FormUtils.getPasswordHelper(this.translate);
  }

  ngOnInit(): void {
    const langSub = this.translate.onLangChange.subscribe(() => (this.passwordHelpers = FormUtils.getPasswordHelper(this.translate)));
    this.subscriptions.push(langSub);

    const passSub = this.password.valueChanges.subscribe((value) => this._onChange(value));
    this.subscriptions.push(passSub);
  }

  ngOnDestroy() {
    this.subscriptions = Utils.unsubscribe(this.subscriptions);
  }

  private _onChange = (_: string) => undefined;
  registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn as any;
  }

  onTouched() {
    this._onTouched();
  }
  private _onTouched = () => undefined;
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.password.disable();
    else this.password.enable();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const isEmpty = control.value == '' || control.value == null;
    if (!isEmpty && !control.touched) {
      control.markAsTouched();
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
    if (!control.touched) return null;

    this.isRequired = control.hasValidator(Validators.required);
    if (this.isRequired && isEmpty) {
      this.isInvalid = true;
      return { required: true };
    }

    const isStrong = REGEX_STRONG_PASSWORD.test(this.password.value);
    if (!isStrong && this.checkStrength && !isEmpty) {
      this.isInvalid = true;
      return { pattern: true };
    }

    if (control.value?.length > 255) {
      this.isInvalid = true;
      return { maxlength: true };
    }

    this.isInvalid = false;
    return null;
  }

  writeValue(value: string): void {
    this.password.patchValue(value);
    if (value != '' && value != null) this._onTouched();
  }
}
